/**
 * The monsters' shots (spec 02 §5): Hidegons' fireball, Drunk's bottle, Mighta's rock, the Invader's
 * laser; about one tile across (the laser a beam 0.4 wide and 1.2 tall), centred on the origin so
 * they can spin. The fireball flies toward +x, its flames trailing back along -x; the renderer
 * mirrors it for a shot flying left.
 */

import { type Matrix4, Vector3 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import { Pcg32 } from '../../sim/rng';
import type { ProjectileKind } from '../../sim/types';
import type { Palette } from '../palette';
import { lathe, Smooth, tube } from './powerups/shapes';

/** A hot ball of fire: an orange shell round a yellow core, a white-hot heart, three tongues of flame behind. */
function fireball(mb: MeshBuilder, p: Palette): void {
  const s = new Smooth();
  const fire = (i: number): ReturnType<Palette['color']> => p.color(`fire.${i}`);
  const tongues = [[0, 0.72, 0.24, 0.06], [0.14, 0.5, 0.14, 0.16], [-0.14, 0.46, 0.13, -0.1]] as const;
  for (const [dy, len, r, lift] of tongues) {
    const path: Vector3[] = [];
    const radii: number[] = [];
    for (let i = 0; i <= 8; i++) {
      const t = i / 8;
      path.push(new Vector3(0.12 - t * len, dy + lift * t * t + Math.sin(t * 5) * 0.03, 0));
      radii.push(i === 8 ? 0.01 : r * (1 - t * 0.85));
    }
    tube(s, path, radii, 10, (i) => fire(i < 5 ? 1 : 0));
  }
  s.flush(mb);
  mb.blob(0.3, 0.3, 0.3, fire(1), place(0.14, 0, 0), { detail: 3 });
  mb.blob(0.22, 0.22, 0.14, fire(2), place(0.2, 0.03, 0.18), { detail: 2 });
  mb.blob(0.11, 0.11, 0.06, fire(3), place(0.25, 0.06, 0.3), { detail: 2 });
}

/**
 * Drunk's bottle: green glass with a cream label and a cork, standing on the origin's centre (0.84
 * long). `m` places it; the boss holds a big one.
 */
export function bottle(mb: MeshBuilder, p: Palette, m?: Matrix4): void {
  const s = new Smooth();
  const profile: Array<[number, number]> = [
    [0, -0.42], [0.17, -0.42], [0.2, -0.38], [0.2, -0.26], [0.2, -0.02], [0.2, 0.06], [0.16, 0.16],
    [0.09, 0.24], [0.075, 0.34], [0.09, 0.36], [0.09, 0.4], [0, 0.4],
  ];
  lathe(s, profile, 16, (seg) =>
    p.color(seg === 3 ? 'puWhite.1' : seg === 8 || seg === 9 ? 'bottle.2' : seg === 0 ? 'bottle.0' : 'bottle.1'));
  s.flush(mb, m);
  const at = (x: number, y: number, z: number): Matrix4 => (m ? m.clone().multiply(place(x, y, z)) : place(x, y, z));
  mb.cylinder(0.065, 0.075, 0.1, 10, p.color('puWood.1'), at(0, 0.38, 0), p.color('puWood.2'));
  mb.blob(0.12, 0.05, 0.03, p.color('puRed.1'), at(0, -0.14, 0.2), { detail: 1 });
  mb.blob(0.04, 0.12, 0.03, p.color('puWhite.2'), at(-0.11, 0.0, 0.16), { detail: 1 });
}

/** A lumpy grey boulder: a rounded ball pushed in and out in broad lumps, smooth-shaded, a few chips. */
function rock(mb: MeshBuilder, p: Palette): void {
  const s = new Smooth();
  const profile: Array<[number, number]> = [];
  for (let i = 0; i <= 10; i++) {
    const phi = (i / 10) * Math.PI;
    profile.push([Math.sin(phi) * 0.42, -Math.cos(phi) * 0.38]);
  }
  const lump = (a: number, y: number): number =>
    1 + 0.1 * Math.sin(3 * a + 1.3) * Math.cos(4 * y + 0.4) + 0.07 * Math.sin(5 * a - 7 * y);
  lathe(s, profile, 18, (seg) => p.color(seg >= 7 ? 'rock.2' : 'rock.1'), lump);
  s.flush(mb);
  const rng = new Pcg32(27);
  for (let i = 0; i < 4; i++) {
    const a = rng.range(0, Math.PI * 2);
    const y = rng.range(-0.2, 0.25);
    const r = 0.38 * Math.sqrt(1 - (y / 0.4) ** 2);
    mb.blob(0.07, 0.05, 0.05, p.color('rock.0'), place(Math.sin(a) * r, y, Math.cos(a) * r, a), { detail: 1 });
  }
}

/** The Invader's laser: a cyan beam with a white-hot core, bright rings along it. */
function laser(mb: MeshBuilder, p: Palette): void {
  const s = new Smooth();
  const profile: Array<[number, number]> = [];
  for (let i = 0; i <= 12; i++) {
    const t = i / 12;
    const y = -0.6 + t * 1.2;
    const end = Math.min(1, (0.6 - Math.abs(y)) / 0.2);
    profile.push([0.2 * Math.sqrt(Math.max(0, end)) * (i === 0 || i === 12 ? 0 : 1), y]);
  }
  lathe(s, profile, 12, (seg) => p.color(seg % 3 === 1 ? 'puCyan.2' : 'puCyan.1'));
  s.flush(mb);
  mb.blob(0.07, 0.5, 0.1, p.color('puWhite.2'), place(0, 0, 0.13), { detail: 2 });
}

/** A monster's shot by kind, centred on the origin. */
export function projectileModel(kind: ProjectileKind, p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  if (kind === 'fireball') fireball(mb, p);
  else if (kind === 'bottle') bottle(mb, p);
  else if (kind === 'rock') rock(mb, p);
  else laser(mb, p);
  return mb;
}

